import { CRAYO_ENTRY, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;

const THEIR_PAGE = "https://storyshort.ai/alternatives/crayo";
const PRICING = "https://storyshort.ai/pricing";
const REFUND = "https://storyshort.ai/refund";
const ABOUT = "https://storyshort.ai/about";
const TOOLS = "https://storyshort.ai/tools";

export const STORYSHORT_CONTENT: CompareContent = {
  slug: "crayo-vs-storyshort",
  competitorSlug: "storyshort",
  title: "Crayo vs StoryShort",
  seoTitle: "Crayo vs StoryShort: Clipping Editor vs Story Video Generator",
  seoDescription:
    "Crayo vs StoryShort, dated prices. Crayo clips podcasts and streams at 1 credit per clip with an editor. StoryShort Starter is $39 for 400 credits, no refunds.",
  cardVerdict:
    "A clipping editor against a story video generator: pick Crayo to clip real footage and edit it, StoryShort for AI characters, series and auto-posting.",
  bestFor: "Clippers weighing an editor against a generator",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick if you clip footage: AutoClip turns a podcast, stream VOD or interview into 1 to 20 ranked, face-aware clips, one of Hobby's ${hobby.credits} credits per finished clip at ${money(hobby.monthly)} a month, with a timeline editor on every plan. StoryShort is the better pick if you want generated story videos with consistent AI characters and a series that writes, renders and posts itself. The dated evidence is below.`,
  pickCrayoIf: [
    "You clip podcasts, streams and interviews: AutoClip takes a YouTube or TikTok link or a file up to 3 hours, ranks the moments and reframes faces into four layouts; StoryShort's long-video tool finds moments but describes no ranking, reframing or layouts.",
    `You keep a few clips from each long source and pay per finished clip: 32 clips a month use 32 of Hobby's ${hobby.credits} credits for ${money(hobby.monthly)}, while StoryShort's credit table prices generated videos at about 15 to 418 credits each and publishes no price for a clip.`,
    "You want to edit the clip on a timeline with a camera track, keyframes, 32 caption styles in 56 spoken languages, and drive all of it from a 34-endpoint API; StoryShort edits by chat regeneration per shot.",
    "You want to see the ranked moments before spending: Find Viral Moments lists up to 50 and a credit is spent only when you cut, and Clip Cutter ranges cost nothing.",
  ],
  pickThemIf: [
    "You want a recurring character who looks the same in every scene, or UGC-style AI ads and avatars. StoryShort sells consistent characters and premium video models; Crayo has no character system and no avatars.",
    "You want the channel to run itself. A StoryShort series picks topics, writes, produces and posts to TikTok, YouTube and Instagram on a schedule. Crayo has no scheduler; you post each file.",
    "You want your own voice cloned or 8 to 10 minute long-form videos. StoryShort clones from a short recording and makes long-form on Influencer and up; Crayo has hundreds of stock voices, no cloning.",
  ],
  wins: [
    {
      claim: "Crayo clips real footage with ranked moments and face-aware reframing; StoryShort generates from scratch.",
      evidence:
        "StoryShort's tools index lists a long-video-to-shorts tool that finds best moments but describes no reframing or layouts, checked Oct 8, 2026. Crayo's AutoClip cuts 1 to 20 clips per run from sources up to 3 hours, with virality ranking, four layouts and a face-aware camera, and it is on every plan.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo's credit cost per clip is flat; StoryShort's rises with the model and the edits.",
      evidence:
        "StoryShort's pricing table prices a 30-second image video at about 15 credits, an ASMR video at 50, an AI movie at 110, a UGC ad at 115 and an avatar video at 270 or more, and a homepage demo shows one premium shot regeneration at 418 credits, more than a whole Starter month. Crayo charges 1 credit per finished clip whatever the layout or length.",
      href: PRICING,
    },
    {
      claim: "Crayo gives more finished clips per dollar than StoryShort gives videos at the entry plan.",
      evidence:
        `StoryShort Starter is $39 a month, or $23 a month billed yearly at $279, for 400 credits, which its pricing page translates to about 26 videos of 30 seconds or 16 of 60 seconds, checked Oct 8, 2026. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} workflow credits, and one credit is one finished clip.`,
      href: PRICING,
    },
    {
      claim: "Crayo includes a timeline editor on every plan; StoryShort edits by chat regeneration.",
      evidence:
        "StoryShort edits by chat-based shot regeneration and its tools index lists no timeline, per storyshort.ai/tools on Oct 8, 2026. Crayo's editor has a camera track, keyframes, zooms, transitions, speed ramps, 32 caption styles with keyword highlight and 56 spoken languages, and it opens every AutoClip clip on Hobby and up.",
      href: TOOLS,
    },
    {
      claim: "Crayo runs support on Discord 24/7 and email with a published correction policy.",
      evidence:
        "StoryShort's about page states that the product is built and run by one person with no growth team and no outsourced support, checked Oct 8, 2026. Crayo answers on Discord around the clock and at support@crayo.ai, and corrects any error on this page within 48 hours.",
      href: ABOUT,
    },
  ],
  concessions: [
    {
      claim: "StoryShort's AI characters stay consistent across scenes and it runs premium video models.",
      evidence:
        "The homepage sells consistent characters, on-screen speaking characters, AI UGC and premium video models with per-shot chat regeneration, checked Oct 8, 2026. Crayo generates AI images and stock-voice narration but has no character system, no avatars and no AI video model.",
      href: "https://storyshort.ai",
    },
    {
      claim: "StoryShort posts for you and can run a series on its own; Crayo does not.",
      evidence:
        "The homepage says a series picks topics, writes, produces and posts on a schedule to TikTok, YouTube and Instagram, with 1 to 4 series by plan on the pricing page. Crayo exports a file and you post it, which StoryShort's page about Crayo correctly says.",
      href: "https://storyshort.ai",
    },
    {
      claim: "StoryShort makes 8 to 10 minute long-form videos and clones your voice.",
      evidence:
        "Long-form videos of 8 to 10 minutes cost 85 to 150 credits on Influencer ($129 a month) and Ultra ($199 a month), and voice cloning from a short recording is on the homepage, checked Oct 8, 2026. Crayo has hundreds of stock voices and no cloning.",
      href: PRICING,
    },
    {
      claim: "StoryShort rolls unused credits over; Crayo resets them monthly.",
      evidence:
        "The StoryShort refund page says unused credits roll over up to 2x the monthly amount, checked Oct 8, 2026. Crayo credits reset each cycle with no rollover; top-up packs are separate.",
      href: REFUND,
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. You post the 32 files yourself.",
    },
    them: {
      plan: "Starter",
      cost: "$39/mo or $23/mo billed yearly ($279/yr)",
      usage:
        "StoryShort's tools index lists a long-video-to-shorts tool, but its credit table prices generated videos, not clips, and publishes no source cap, so what 32 clips cost is not published; the same 400 credits buy about 16 generated one-minute videos",
      note: "Tool listing and per-video credit costs from storyshort.ai/tools and the pricing page, Oct 8, 2026.",
    },
  },
  test: {
    status: "scheduled",
    source:
      "A 20-minute single-speaker talking-head video owned by Crayo, with the same written topic brief given to both tools",
    plans: "StoryShort Starter and Crayo Clipper, same file where accepted, same day, defaults first then one tuned run each",
  },
  billingNote:
    "StoryShort subscriptions and credit purchases are non-refundable, with a discretionary refund within 15 days when zero credits were used and billing errors always refunded. Unused credits roll over up to 2x the monthly amount, and yearly plans are billed up front, from $279 to $1,399.",
  corrections: [
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim:
        'Publishing is still on you, and the table marks Crayo No for a built-in scheduling calendar, native publishing to TikTok, YouTube Shorts and Reels, and series that generate videos daily.',
      reality:
        "Correct and still current. Crayo has no scheduler, no auto-posting and no daily series; you download the file and post it. View Tracker follows other creators and is not a scheduler.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim: "The alternatives hub lists Crayo as having free sign-up.",
      reality: `Crayo has no free tier and no trial. The entry plan is Hobby at ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year. Twelve free browser tools at crayo.ai/features/free need no account, but no plan credits are free.`,
      evidenceHref: "https://crayo.ai/pricing",
      evidenceLabel: "Crayo pricing",
    },
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim: 'The table calls Crayo "Shorts focused" and lists no clipping row at all.',
      reality:
        "Crayo's AutoClip turns YouTube and TikTok links or uploads of 1 minute to 3 hours into 1 to 20 ranked clips with face-aware reframing, four layouts and virality ranking, on every plan.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "AutoClip",
    },
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim: "The table omits any editor row for Crayo.",
      reality:
        "Every Crayo plan includes a timeline editor with a camera track, keyframes, zooms, transitions, speed ramps, looks, 32 caption styles and keyword highlight, exporting up to 1080p.",
      evidenceHref: "https://crayo.ai/features/auto-clip",
      evidenceLabel: "Crayo editor",
    },
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim: "The table omits any API row for Crayo.",
      reality:
        "Crayo publishes a public API with 34 endpoints under /v1 covering autoclip, exports, voiceover, images, face swap and webhooks, available on every plan with one key per account.",
      evidenceHref: "https://crayo.ai/developers",
      evidenceLabel: "Crayo developers",
    },
    {
      site: "StoryShort",
      url: THEIR_PAGE,
      claim: "The table omits View Tracker and Enhance Speech.",
      reality:
        "Crayo's View Tracker follows Instagram, YouTube and TikTok creators, their cadence and viral outliers, on 100 to 2,500 monthly tracker credits by plan. Enhance Speech cleans up spoken audio before it goes in a video. Both are on every plan.",
      evidenceHref: "https://crayo.ai/features/speech-enhancer",
      evidenceLabel: "Enhance Speech",
    },
  ],
  faqs: [
    {
      question: "Is Crayo cheaper than StoryShort for clipping?",
      answer:
        `Per finished clip, yes. Crayo Hobby is ${money(hobby.monthly)} a month or ${money(hobby.yearlyTotal)} a year for ${hobby.credits} workflow credits, and one credit is one finished clip, so 32 clips a month use 32 credits. StoryShort Starter is $39 a month, or $23 a month billed yearly at $279, for 400 credits, which its pricing page says is about 26 videos of 30 seconds or 16 of 60 seconds, checked Oct 8, 2026; 32 generated one-minute videos would need Growth at $69 a month, or $42 yearly. What StoryShort's price buys that Crayo's does not is the posting and the AI characters.`,
    },
    {
      question: "Can StoryShort clip a podcast or stream like Crayo's AutoClip?",
      answer:
        "Partly. StoryShort's tools index, checked Oct 8, 2026, lists a long-video-to-shorts tool among its 56 tools that finds best moments, but the site shows no ranking, describes no face reframing or layouts, and publishes no source length cap or per-clip credit cost. Crayo's AutoClip takes a YouTube or TikTok link or a file up to 3 hours, returns 1 to 20 ranked clips of 15 to 120 seconds with a face-aware camera and four layouts, and Find Viral Moments lists up to 50 moments before a credit is spent. Clip Cutter cuts manual ranges for free.",
    },
    {
      question: "Does StoryShort have a free trial or free plan?",
      answer:
        "Not a published one. StoryShort.ai says signing up is free, but credits require a paid plan and no free credit amount appears on the pricing page or homepage as of Oct 8, 2026. Its refund page says only that if a free trial is offered you are not charged during it. Crayo has no free plan and no trial either; the entry plan is Hobby, and twelve free browser tools at crayo.ai/features/free need no account. Neither product lets you test paid work for free, so the per-clip credit math above is the thing to compare.",
    },
    {
      question: "Does Crayo auto-post like StoryShort?",
      answer:
        "No. Crayo exports a file and you post it yourself. StoryShort's homepage says it posts to TikTok, YouTube and Instagram and that a series can pick topics, write, produce and post on a schedule, with 1 to 4 series depending on plan. StoryShort's page about Crayo says publishing is still on you, and that is accurate. If hands-off posting is the deciding feature, StoryShort wins that row and Crayo does not compete on it.",
    },
    {
      question: "How many videos do 400 StoryShort credits actually make?",
      answer:
        "It depends on the mode. StoryShort's pricing page prices a 30-second image video at about 15 credits and a 60-second one at about 25, which gives the stated 26 or 16 videos a month on Starter. ASMR videos cost about 50, AI movies 110, UGC ads 115 and avatar videos 270 or more, so a Starter month of avatar videos is one video. A homepage demo shows a single premium shot regeneration at 418 credits. The estimate is shown before you generate, and unused credits roll over up to 2x the monthly amount.",
    },
    {
      question: "Does StoryShort offer refunds? Does Crayo?",
      answer:
        "StoryShort's refund page says subscriptions and credit purchases are non-refundable, with a discretionary refund within 15 days if zero credits were used, and billing errors or charges after cancellation always refunded, checked Oct 8, 2026. Crayo's policy is no refunds, all sales final. Crayo cancels in one click from Settings with the plan active to the end of the cycle; StoryShort's pages do not describe the cancellation mechanism.",
    },
    {
      question: "Does Crayo have consistent characters or voice cloning like StoryShort?",
      answer:
        "No. StoryShort's homepage sells consistent characters across scenes, on-screen speaking characters, AI UGC, talking avatars and voice cloning from a short recording, checked Oct 8, 2026. Crayo generates AI images, narrates with hundreds of stock voices, and has a voice changer and Enhance Speech, but it cannot keep one AI character consistent across videos, has no avatars and cannot clone your voice. If a recurring AI character is the point of the channel, StoryShort wins that row.",
    },
    {
      question: "Does Crayo also make story or text videos like StoryShort?",
      answer:
        `Yes, as a side feature. StoryShort has a Reddit post to video tool among its 56 tools and prices a 60-second image video at about 25 credits, so Starter covers about 16 a month; none of its 56 tool pages covers iMessage-style fake text videos, per storyshort.ai/tools on Oct 8, 2026. Crayo's Reddit story and fake text generators are on every plan at 1 credit each from the same ${hobby.credits}-credit pool, with ${hobby.voiceoverMinutes} voiceover minutes on Hobby. Crayo is built around clipping; the generators are extras.`,
    },
    {
      question: "Is what StoryShort says about Crayo accurate?",
      answer:
        `Partly. StoryShort's alternatives page is right that Crayo has no scheduler, no native publishing and no daily series, and that you post each file yourself. Its hub lists Crayo as having free sign-up, but Crayo has no free tier; Hobby is ${money(hobby.monthly)} a month. The table also leaves out AutoClip clipping with face-aware reframing, the timeline editor, the 34-endpoint API, View Tracker and Enhance Speech, all on every Crayo plan as of October 25, 2026. The corrections box above answers each line with a link.`,
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. StoryShort prices checked Oct 8, 2026: Starter $39/mo or $23/mo billed yearly ($279/yr) for 400 credits, Growth $69/mo or $42/mo yearly for 1,200 credits, Ultra $199/mo or $117/mo yearly for 5,000 credits.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-autoshorts", "crayo-vs-faceless-video", "crayo-vs-revid"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "storyshort alternative",
    "storyshort vs crayo",
    "crayo vs storyshort",
    "storyshort pricing",
    "storyshort review",
  ],
};
